import { Feature } from 'src/replicant/analytics';
import { GiftOrigin, trackGiftSend, trackGiftSendAttempt } from './analytics';
import StateObserver, { user, now } from 'src/StateObserver';
import { GiftType } from 'src/replicant/gifts';
import * as Gifts from 'src/replicant/gifts';
import { withLoading } from 'src/lib/utils';

/**
 * Send one gift over OA chatbot message
 */
export async function sendGiftOA(
  receiverID: string,
  giftType: GiftType,
  subFeature: string,
  origin: GiftOrigin,
) {
  trackGiftSendAttempt({
    origin,
    isMassSent: false,
    messagesCount: 1,
    type: 'oa',
    gift: giftType,
  });

  await withLoading(
    () =>
      StateObserver.invoke.asyncSendGiftsOaMessage({
        receiverIds: [receiverID],
        subFeature,
      }),
    {
      feature: Feature.GIFTS._,
      subFeature: Feature.GIFTS.SEND_GIFTS_OA_MESSAGE,
    },
  );

  const amount = getGiftAmount(giftType);
  trackGiftSend({
    gift: giftType,
    amount,
    receiver: receiverID,
    type: 'oa',
    origin,
  });
}

/**
 * Retrieve the amount of a gift per gift type
 */
function getGiftAmount(giftType: GiftType) {
  const { energyGiftAmount, bpointGiftAmount, gemGiftAmount } = Gifts.ruleset;
  const amountMap: Record<GiftType, number> = {
    energy: energyGiftAmount,
    bPoint: bpointGiftAmount,
    gems: gemGiftAmount,
  };
  return amountMap[giftType];
}

/**
 * Send mass gifts over OA chatbot messages
 */
export async function sendMassGiftsShare(
  receiverIDs: string[],
  giftType: GiftType,
  origin: GiftOrigin,
) {
  trackGiftSendAttempt({
    origin,
    isMassSent: true,
    messagesCount: receiverIDs.length,
    type: 'oa',
    gift: giftType,
  });

  const subFeatureMap: Record<GiftType, string> = {
    energy: Feature.GIFTS.SEND_ENERGY_ALL,
    bPoint: Feature.GIFTS.SEND_BPOINTS_ALL,
    gems: Feature.GIFTS.SEND_GEMS_ALL,
  };

  const $subFeature = subFeatureMap[giftType];

  // Receivers whose OA message was actually dispatched. Each batch is its own async
  // action, so a failure part way through leaves the earlier batches delivered.
  const sent: string[] = [];

  try {
    // Send all gifts as OA messages, in bounded batches. Every receiver becomes one
    // LINE push with no server-side concurrency limit, so an unbounded list times
    // out (#11690). Async actions flush on their own, no explicit flush needed.
    //
    // The blocking loader stays, and has to. Each async action holds the client
    // replicant queue lock, so every other invoke the player triggers sits
    // unexecuted until this finishes. Dropping the loader would not let them carry
    // on, it would only stop telling them why nothing responds.
    await withLoading(
      async () => {
        const { giftOaBatchSize } = Gifts.ruleset;

        for (
          let index = 0;
          index < receiverIDs.length;
          index += giftOaBatchSize
        ) {
          const batch = receiverIDs.slice(index, index + giftOaBatchSize);

          await StateObserver.invoke.asyncSendGiftsOaMessage({
            receiverIds: batch,
            subFeature: $subFeature,
          });

          sent.push(...batch);
        }
      },
      {
        feature: Feature.GIFTS._,
        subFeature: Feature.GIFTS.SEND_GIFTS_OA_MESSAGE,
      },
    );
  } finally {
    // Track the batches that were dispatched, rather than nothing at all: a single
    // failed call used to suppress GiftSend for the entire list. Still optimistic
    // per recipient — the action does not report which LINE pushes actually
    // landed, so a batch that resolves is counted in full.
    const amount = getGiftAmount(giftType);
    sent.forEach((receiverID) => {
      trackGiftSend({
        gift: giftType,
        amount,
        receiver: receiverID,
        type: 'oa',
        origin,
      });
    });
  }

  return sent;
}
